/* eslint-disable camelcase */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@glu/theming';
import isIE11 from 'utils/isIE11';
import checkmark from 'styles/icons/checkmark.svg';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Spinner from 'common/Spinner';
import { SOCURE_RESPONSE_STATUS, VERIFICATION_STAGES } from 'config/socure';
import fieldActions from 'core/fields/actions';
import poweredBy from '../icons/powered-by-socure.jpg';

const styles = ({ oaoButtons: buttons, palette, spacing, typography }) => ({
  description: {
    margin: [0, 'auto'],
    marginBottom: spacing.lg,
    maxWidth: 400,
    textAlign: 'center',
  },
  error: {
    color: palette.error,
    margin: [0, 'auto'],
    marginBottom: spacing.lg,
    maxWidth: 400,
    textAlign: 'center',
  },
  help: {
    ...typography.p2,
    margin: 0,
    padding: 0,
    textAlign: 'center',
  },
  poweredBy: {
    display: 'block',
    margin: [0, 'auto'],
    marginBottom: spacing.xs,
    position: 'relative',
  },
  progress: {
    display: 'flex',
    justifyContent: 'space-evenly',
    margin: [0, 'auto', 50],
    maxWidth: 400,
  },
  progressCircle: {
    '&.filled': {
      '&::before': {
        background: isIE11
          ? `transparent url(${checkmark}) 0 50% no-repeat`
          : buttons.primary.color,
        backgroundPosition: '50%',
        backgroundRepeat: 'no-repeat',
        content: '""',
        display: 'block',
        height: 16,
        left: 4,
        maskImage: isIE11 ? 'none' : `url(${checkmark})`,
        position: 'relative',
        top: 4,
        width: 16,
      },
      background: buttons.primary.background,
      border: 'none',
      position: 'relative',
    },
    border: `1px solid ${palette.form.border}`,
    borderRadius: '50%',
    display: 'block',
    height: 24,
    margin: [0, 'auto'],
    marginBottom: 5,
    width: 24,
  },
  progressItem: {
    '& p': {
      ...typography.p3,
      fontWeight: typography.fontWeights.bold,
      margin: 0,
      padding: 0,
    },
    maxWidth: 100,
    textAlign: 'center',
  },
  qrCode: {
    display: 'block',
    height: 112,
    margin: [0, 'auto', spacing.unit * 2],
    width: 112,
  },
  spinner: {
    '& > .spinner': {
      position: 'relative !important',
    },
    display: 'block',
    margin: [0, 'auto'],
    position: 'relative',
    textAlign: 'center',
  },
});

const DesktopFlow = ({ classes, fieldDescr, help }) => {
  // --------get data from state-------
  const {
    configs: { socureApiKey, socureStatus = undefined },
    data,
    resources: { socureErrorMsg: errorMsg = '' },
  } = useSelector((props) => props, shallowEqual);

  // get cellphone number
  const cellPhone = '_phone_cellularPhoneNumber';
  const cellPhoneNumberKey = Object.keys(data).find((key) => key.includes(cellPhone));
  const socureCellNumber = cellPhoneNumberKey ? `+1${data[cellPhoneNumberKey]}` : '';

  // useDispatch
  const dispatch = useDispatch();

  // useStates
  const [error, setError] = useState(false);
  const [qrCode, setQrCode] = useState(undefined);
  const [socureResponse, setSocureResponse] = useState(undefined);
  const [dataFetchedinThisSession, setDataFetchedinThisSession] = useState(false);

  const mountedRef = useRef(false);

  // useEffects
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (socureResponse?.status && socureResponse.qrcode) setQrCode(socureResponse.qrcode);
  }, [dispatch, socureResponse]);

  useEffect(() => {
    if (mountedRef.current) {
      let initiated = false;
      window.SocureInitializer.init(socureApiKey).then((lib) => {
        if (!initiated) {
          window.Socure.init(socureApiKey, '#socureEmbedId', {
            onError: (resp) => {
              setSocureResponse((s) => ({ ...resp, qrcode: resp.qrcode || s?.qrcode }));
              dispatch(fieldActions.socureAutocomplete(resp));
              setError(true);
              window.Socure.cleanup();
              window.Socure.unmount();
            },
            onProgress: (resp) => {
              setSocureResponse((s) => ({ ...resp, qrcode: resp.qrcode || s?.qrcode }));
            },
            onSuccess: (resp) => {
              setSocureResponse((s) => ({ ...resp, qrcode: resp.qrcode || s?.qrcode }));
              dispatch(
                fieldActions.socureAutocomplete(resp?.status === 'DOCUMENTS_UPLOADED' && resp),
              );
              setDataFetchedinThisSession(true);
              window.Socure.cleanup();
              window.Socure.unmount();
            },
            qrCodeNeeded: true,
          })
            .then(() => {
              initiated = true;
              let started = false;
              if (!started) {
                window.Socure.start(1, socureCellNumber, null)
                  .then((response) => {
                    dispatch(fieldActions.socureAutocomplete(response));
                    setSocureResponse(response);
                    started = true;
                  })
                  .catch((err) => {
                    // eslint-disable-next-line no-alert
                    console.log(JSON.stringify(err));
                    setError(true);
                  });
              }
            })
            .catch((err) => {
              // eslint-disable-next-line no-alert
              console.log(JSON.stringify(err));
              setError(true);
            })
            .finally(() => {
              window.Socure.reset();
            });
        }
      });
    }

    return () => {
      if (window.Socure) {
        setSocureResponse(undefined);
        window.Socure.reset();
        window.Socure = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (socureStatus === SOCURE_RESPONSE_STATUS.DOCUMENTS_UPLOADED && !dataFetchedinThisSession) {
    return <></>;
  }

  if (error) {
    return <p className={classes.error}>{errorMsg}</p>;
  }

  return (
    <div className="form-group">
      <div id="socureEmbedId" className="hidden" />
      {qrCode && (
        <>
          {fieldDescr && <p className={classes.description}>{fieldDescr}</p>}
          <img src={qrCode} className={classes.qrCode} alt="Socure QR code" />
        </>
      )}
      {!qrCode ? (
        <div className={classes.spinner}>
          <Spinner loading type="page" />
        </div>
      ) : (
        <>
          <img src={poweredBy} className={classes.poweredBy} alt="Powered by Socure" />
          <div className={classes.progress}>
            {VERIFICATION_STAGES.map((vValue, i) => {
              const shouldFill = () => {
                if (!socureResponse.status) {
                  return null;
                }
                if (
                  socureResponse.status === SOCURE_RESPONSE_STATUS.WAITING_FOR_UPLOAD &&
                  i === 0
                ) {
                  return true;
                }
                if (
                  (socureResponse.status === SOCURE_RESPONSE_STATUS.DOCUMENTS_UPLOADED ||
                    socureResponse.status === SOCURE_RESPONSE_STATUS.VERIFYING) &&
                  (i === 0 || i === 1)
                ) {
                  return true;
                }
                return false;
              };
              return (
                <div key={`progress-item-${vValue}`} className={classes.progressItem}>
                  <div className={classNames(classes.progressCircle, { filled: shouldFill() })}>
                    &nbsp;
                  </div>
                  <p>{vValue}</p>
                </div>
              );
            })}
          </div>
          <p className={classes.help}>{help}</p>
        </>
      )}
    </div>
  );
};

DesktopFlow.propTypes = {
  fieldDescr: PropTypes.string,
  help: PropTypes.string,
};

DesktopFlow.defaultProps = {
  fieldDescr: '',
  help: '',
};

export default withStyles(styles)(DesktopFlow);
